/** Minimal slice of `DOMRect` the tooltip positioner needs. */
export interface AnchorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Position a view-widget tooltip relative to its anchor widget, clamped so it
 * never runs off-screen.
 *
 * View widgets live inside the view panel's `overflow: hidden` box, so a
 * tooltip anchored to a widget at a panel border used to be truncated. The
 * shared tooltip node now lives on `<body>` with `position: fixed`, and this
 * computes its viewport coordinates: centred above the widget by default,
 * flipped below when there isn't room above, and clamped horizontally and
 * vertically into the viewport (mirrors GraphTooltip's clamping).
 */
export function computeWidgetTooltipPosition(
  anchor: AnchorRect,
  tipW: number,
  tipH: number,
  vw: number,
  vh: number,
  gap = 6,
  margin = 8,
): { left: number; top: number } {
  // Centre over the widget, then clamp into [margin, vw - tipW - margin].
  let left = anchor.left + anchor.width / 2 - tipW / 2;
  left = Math.max(margin, Math.min(left, vw - tipW - margin));

  // Prefer above the widget; flip below when there isn't room above, then clamp
  // so the bottom edge stays on-screen.
  let top = anchor.top - tipH - gap;
  if (top < margin) {
    const below = anchor.top + anchor.height + gap;
    top = below + tipH + margin <= vh ? below : Math.max(margin, vh - tipH - margin);
  }
  return { left, top };
}
